/**
 * PassVectorTriage · Phase A · Human-in-the-Loop UI.
 *
 * Lists every auto-proposed (or manually-drawn) pass-vector on a clip
 * and gives the admin three triage actions per row:
 *
 *   🟢 ACCEPT  → POST /pass-vectors/{vid}/verify   → status = "verified"
 *   🟡 EDIT    → opens an inline re-draw overlay   → status = "corrected"
 *   🔴 REJECT  → POST /pass-vectors/{vid}/reject   → status = "rejected"
 *
 * Plus a 🗑 DELETE shortcut for cleanup of rejected/manual mistakes
 * (DELETE /pass-vectors/{vid}).
 *
 * Two bulk shortcuts live in the header BulkTriageBar:
 *
 *   ✓ ACCEPT ALL ≥ X%  →  POST /pass-vectors/bulk-verify
 *   ✗ REJECT ALL < Y%  →  POST /pass-vectors/bulk-reject
 *
 * Render contract:
 *   • Always shows every vector — including rejected — so the audit
 *     trail stays visible. Rejected rows are dimmed.
 *   • Verified + corrected rows get a green ring; unverified rows get
 *     a yellow ring to scream "pending triage".
 *   • The list re-fetches on mount and after every mutation so we
 *     stay in sync with the Mongo state. Mutations are NOT optimistic
 *     by design — a coach mis-clicking an Accept on the wrong line is
 *     a worse failure mode than a 200 ms latency blip.
 *
 * Architectural note: the existing `ClipPassVectorEditor` continues to
 * own the LEGACY singular `clip.pass_vector` flow (one-shot manual
 * draw). This triage panel owns the NEW `pass_vectors[]` list. The two
 * surfaces live side-by-side until the legacy field can be retired.
 *
 * Phase A · Step 5 refactor: the row, bulk bar, harvest strip, and
 * re-draw overlay all live in `./triage/` so this container stays
 * focused on state + network calls only.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Loader2, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  listClipPassVectors,
  verifyClipPassVector,
  rejectClipPassVector,
  correctClipPassVector,
  deleteClipPassVector,
  createClipPassVector,
  triggerHarvest,
  bulkVerifyClipPassVectors,
  bulkRejectClipPassVectors,
  fehlerText,
} from "@/lib/api";

import {
  BulkTriageBar,
  PassVectorRedrawOverlay,
  PassVectorRow,
} from "./triage";

export default function PassVectorTriage({ clip, parent, onMutated, onJumpToRelease, getVideoTime, currentTime, onNudgeFrame }) {
  const [vectors, setVectors] = useState(clip.pass_vectors || []);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);           // mutating vector ID
  const [editingId, setEditingId] = useState(null);     // vector currently in re-draw mode
  const [acceptThreshold, setAcceptThreshold] = useState(0.85);
  const [rejectThreshold, setRejectThreshold] = useState(0.55);
  const [bulkBusy, setBulkBusy] = useState(false);
  // 🆕 2026-02-26 · Multi-pass support. When set, an inline "draw a
  // fresh pass" overlay is rendered above the candidate list and the
  // operator can pin R + C without touching the legacy singular
  // `clip.pass_vector` field — keeps each throw in its own row of
  // `clip.pass_vectors[]` instead of overwriting the previous one.
  const [creatingNew, setCreatingNew] = useState(false);
  // 🆕 2026-02-28 v2 · Live Timeline Sync. We *derive* the clip-local
  // release timestamp from the main video player's live `currentTime`
  // prop instead of snapshotting it on click. This means scrubbing the
  // player AFTER opening the canvas updates the backdrop frame in
  // real-time, letting the coach fine-tune the exact release moment
  // visually. The persist value is captured at submit time (not at
  // click time) so the stored `release_t` always matches whatever
  // frame is currently rendered when the operator saves. The overlay
  // itself handles "freeze on first pin placed" so coordinates don't
  // drift relative to a moving backdrop.
  const liveClipLocalT = useMemo(() => {
    if (typeof currentTime !== "number" || !Number.isFinite(currentTime)) {
      return null;
    }
    const clipStart = typeof clip.start_s === "number" ? clip.start_s : 0;
    const clipEnd = typeof clip.end_s === "number" ? clip.end_s : null;
    const clipDuration = clipEnd != null
      ? Math.max(0, clipEnd - clipStart)
      : (typeof clip.duration_s === "number" ? clip.duration_s : 0);
    const rel = currentTime - clipStart;
    return Math.max(0, Math.min(clipDuration, rel));
  }, [currentTime, clip.start_s, clip.end_s, clip.duration_s]);
  // Ref on the inline create-overlay wrapper so we can pull it into
  // the viewport when the operator clicks `+ Add New Pass`. The
  // panel often sits below the fold (calibration frame can be tall),
  // and without an auto-scroll the coach can't see — let alone click
  // — the canvas to drop the R/C pins. See iteration_22 UX note.
  const createOverlayRef = useRef(null);
  // 🆕 Broadcast-Workflow (2026-02): Triage wird freigegeben, sobald
  // *irgendeine* nutzbare Homographie-Quelle existiert — nicht mehr
  // strikt an das globale 4-Punkt-`parent.calibration.homography`
  // gekoppelt. Gültige Quellen:
  //   1. Klassische Basis-H auf dem Parent-Match (Legacy-Flow), ODER
  //   2. Mindestens ein `joint`-Anker mit eigener H im Parent-Match
  //      (frei-gezeichnet / dl_auto — der neue Broadcast-Flow), ODER
  //   3. Für diese Sequenz existiert bereits ein berechneter
  //      Pass-Vektor mit Meterlänge — dann *war* die H nachweislich
  //      vorhanden und die Triage muss sofort ohne Warnbanner offen
  //      sein, damit der Coach den bereits erzeugten Vektor
  //      verifizieren kann.
  const parentCalibrated = !!(parent?.calibration?.homography)
    || ((parent?.calibration?.extra_zones || []).some(
      (z) => z?.status === "joint" && z?.homography,
    ))
    || (Array.isArray(clip?.pass_vectors) && clip.pass_vectors.length > 0)
    || !!clip?.pass_vector;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listClipPassVectors(clip.id);
      setVectors(list);
    } catch (err) {
      toast.error(fehlerText(err) || "Could not load pass vectors");
    } finally {
      setLoading(false);
    }
  }, [clip.id]);

  useEffect(() => { refresh(); }, [refresh]);

  // 🐛 HOTFIX 2026-02-27 · Bug #1 — Reactive prop sync.
  // The parent SequenceLab re-fetches the clips list whenever
  // `ClipPassVectorEditor` saves a manual pass-vector (`onSaved` →
  // `onRefresh`). That re-render hands US a fresh `clip` prop with
  // the new pass_vectors[] entry, BUT the `refresh()` callback
  // above is keyed off `[clip.id]` (unchanged), so its useEffect
  // never re-fires and the local `vectors` state stays stale.
  // Net result: the badge keeps showing "0 candidates" until the
  // coach hard-reloads the page.
  //
  // Fix: also re-sync `vectors` whenever the parent ships a new
  // `clip.pass_vectors` array — this catches manual draws + any
  // other out-of-band mutation routed through SequenceLab.refresh()
  // (re-harvest, bulk verify, delete, etc.).
  useEffect(() => {
    if (Array.isArray(clip.pass_vectors)) {
      setVectors(clip.pass_vectors);
    }
  }, [clip.pass_vectors]);

  // ── Per-row handlers ────────────────────────────────────────────
  const onAccept = async (v) => {
    setBusyId(v.id);
    try {
      await verifyClipPassVector(clip.id, v.id);
      toast.success(`Pass ${v.thrower_player_id ?? "?"}→${v.catcher_player_id ?? "?"} · accepted`);
      await refresh();
      onMutated?.();
    } catch (err) {
      toast.error(fehlerText(err) || "Accept failed");
    } finally {
      setBusyId(null);
    }
  };

  const onReject = async (v) => {
    setBusyId(v.id);
    try {
      await rejectClipPassVector(clip.id, v.id);
      toast.success("Pass marked as rejected");
      await refresh();
      onMutated?.();
    } catch (err) {
      toast.error(fehlerText(err) || "Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (v) => {
    if (!window.confirm("Permanently delete this pass vector? Audit trail will lose this row.")) return;
    setBusyId(v.id);
    try {
      await deleteClipPassVector(clip.id, v.id);
      toast.success("Pass vector deleted");
      await refresh();
      onMutated?.();
    } catch (err) {
      toast.error(fehlerText(err) || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const onEdit = (v) => setEditingId(v.id);
  const onCancelEdit = () => setEditingId(null);

  const onCorrected = async (v, start, end) => {
    setBusyId(v.id);
    try {
      await correctClipPassVector(clip.id, v.id, start, end);
      toast.success("Pass corrected · status flipped to corrected");
      setEditingId(null);
      await refresh();
      onMutated?.();
    } catch (err) {
      toast.error(fehlerText(err) || "Correction failed");
    } finally {
      setBusyId(null);
    }
  };

  // ── "+ Add New Pass" flow ────────────────────────────────────────
  // Spawns a fresh `pass_vectors[]` entry instead of overwriting the
  // legacy singular `clip.pass_vector`. Mutually exclusive with the
  // per-row edit overlay so the operator never has two redraw
  // surfaces fighting over the calibration canvas.
  //
  // 🆕 Two-Timestamp Anchoring (2026-02-28 v4): the canvas backdrop
  // ALWAYS tracks the main video player's `currentTime` live (no
  // freeze on first pin). The overlay snapshots the clip-local time
  // independently at each pin-placement event and forwards both to
  // this submit handler. We persist BOTH via the new API contract
  // (`createClipPassVector(clipId, start, end, releaseT, catchT)`) so
  // the 3-D parabola fitter can compute the true ∆t between throw
  // and catch — critical for ML-grade physics.
  const onAddNew = () => {
    setEditingId(null);
    setCreatingNew(true);
  };
  const onCancelCreate = () => {
    setCreatingNew(false);
  };
  const onCreateSubmit = async (start, end, releaseT, catchT) => {
    // Defensive fallback: if the overlay was opened without a live
    // `currentTime` driver (legacy callers, deep-link scenarios), the
    // pin snapshots may be `null`. Fall back to `liveClipLocalT`
    // computed from the current prop so the backend still gets a
    // valid `release_t`. `catch_t` stays null in that case — the
    // backend treats absent `catch_t` as "single-timestamp legacy".
    const persistedReleaseT = releaseT != null ? releaseT : liveClipLocalT;
    const persistedCatchT = catchT;
    try {
      const created = await createClipPassVector(
        clip.id, start, end, persistedReleaseT, persistedCatchT,
      );
      const lenLabel = (typeof created?.length_m === "number")
        ? ` · ${created.length_m.toFixed(1)} m`
        : "";
      const tParts = [];
      if (typeof persistedReleaseT === "number") tParts.push(`R@${persistedReleaseT.toFixed(2)}s`);
      if (typeof persistedCatchT === "number") tParts.push(`C@${persistedCatchT.toFixed(2)}s`);
      const tLabel = tParts.length ? ` · ${tParts.join(" → ")}` : "";
      toast.success(`New pass vector created${lenLabel}${tLabel}`);
      setCreatingNew(false);
      await refresh();
      onMutated?.();
    } catch (err) {
      toast.error(fehlerText(err) || "Could not create pass vector");
    }
  };

  // When the create overlay mounts, scroll it into the centre of the
  // viewport so the calibration canvas is fully visible — without
  // this the click-to-pin canvas can sit below the fold on tall
  // sequence-lab layouts (iteration_22 UX finding).
  useEffect(() => {
    if (creatingNew && createOverlayRef.current?.scrollIntoView) {
      createOverlayRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [creatingNew]);

  const onRetriggerHarvest = async (v) => {
    setBusyId(v.id);
    try {
      await triggerHarvest(clip.id, v.id);
      toast.success("Harvest re-queued · slice + B2 backup running in the background");
      setTimeout(() => { refresh(); onMutated?.(); }, 4000);
    } catch (err) {
      toast.error(fehlerText(err) || "Re-harvest failed");
    } finally {
      setBusyId(null);
    }
  };

  // ── Bulk-Triage handlers (Phase A · Step 4 + Step 5) ────────────
  // Eligibility derived live from `vectors` + thresholds so the header
  // counters stay in sync with whatever the coach is sliding to.
  const acceptEligible = useMemo(
    () => vectors.filter(
      (v) => v.status === "unverified"
          && typeof v.confidence === "number"
          && v.confidence >= acceptThreshold,
    ),
    [vectors, acceptThreshold],
  );
  const rejectEligible = useMemo(
    () => vectors.filter(
      (v) => v.status === "unverified"
          && typeof v.confidence === "number"
          && v.confidence < rejectThreshold,
    ),
    [vectors, rejectThreshold],
  );
  const unverifiedTotal = useMemo(
    () => vectors.filter((v) => v.status === "unverified").length,
    [vectors],
  );

  const onBulkVerify = async () => {
    if (!acceptEligible.length) {
      toast.error("No unverified vectors meet the threshold");
      return;
    }
    const confirmMsg =
      `Accept ${acceptEligible.length} unverified pass-vector(s) with `
      + `confidence ≥ ${Math.round(acceptThreshold * 100)}% and auto-harvest each? `
      + "This will also trigger FFmpeg slicing + B2 uploads in the background.";
    if (!window.confirm(confirmMsg)) return;
    setBulkBusy(true);
    try {
      const res = await bulkVerifyClipPassVectors(clip.id, acceptThreshold);
      toast.success(
        `${res.verified_count} pass-vector(s) verified · `
        + `${res.harvest_queued} harvest task(s) queued`,
      );
      await refresh();
      onMutated?.();
      // Background harvests take a few seconds — poll once for the manifest
      // strip refresh so the coach sees the harvest status live.
      setTimeout(() => { refresh(); onMutated?.(); }, 4500);
    } catch (err) {
      toast.error(fehlerText(err) || "Bulk verify failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const onBulkReject = async () => {
    if (!rejectEligible.length) {
      toast.error("No unverified vectors fall below the threshold");
      return;
    }
    const confirmMsg =
      `Reject ${rejectEligible.length} unverified pass-vector(s) with `
      + `confidence < ${Math.round(rejectThreshold * 100)}%? They will be `
      + "hidden from the dashboard but kept for audit. No harvest runs.";
    if (!window.confirm(confirmMsg)) return;
    setBulkBusy(true);
    try {
      const res = await bulkRejectClipPassVectors(clip.id, rejectThreshold);
      toast.success(`${res.rejected_count} low-confidence pass-vector(s) rejected`);
      await refresh();
      onMutated?.();
    } catch (err) {
      toast.error(fehlerText(err) || "Bulk reject failed");
    } finally {
      setBulkBusy(false);
    }
  };

  if (!parentCalibrated) {
    return (
      <div className="border border-amber-400/40 bg-amber-400/5 rounded-sm p-4">
        <div className="font-mono text-[11px] uppercase tracking-widest text-amber-300 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" />
          Parent match needs calibration before pass vectors can be triaged.
        </div>
      </div>
    );
  }

  const counts = {
    unverified: vectors.filter((v) => v.status === "unverified").length,
    verified: vectors.filter((v) => v.status === "verified").length,
    corrected: vectors.filter((v) => v.status === "corrected").length,
    rejected: vectors.filter((v) => v.status === "rejected").length,
  };

  return (
    <div
      data-testid={`pass-vector-triage-${clip.id}`}
      className="border border-primary/40 bg-card rounded-sm overflow-hidden"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-secondary/30">
        <div className="font-display font-black uppercase tracking-widest text-xs text-primary flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" />
          Pass-Vector Triage · {vectors.length} {vectors.length === 1 ? "candidate" : "candidates"}
        </div>
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest">
          {counts.unverified > 0 && (
            <Badge className="bg-amber-400/15 text-amber-300 border-amber-400/40 border">
              {counts.unverified} pending
            </Badge>
          )}
          {counts.verified > 0 && (
            <Badge className="bg-emerald-400/15 text-emerald-300 border-emerald-400/40 border">
              {counts.verified} verified
            </Badge>
          )}
          {counts.corrected > 0 && (
            <Badge className="bg-amber-300/15 text-amber-200 border-amber-300/40 border">
              {counts.corrected} corrected
            </Badge>
          )}
          {counts.rejected > 0 && (
            <Badge className="bg-rose-400/15 text-rose-300 border-rose-400/40 border">
              {counts.rejected} rejected
            </Badge>
          )}
          {/* + Add New Pass — multi-pass UX (2026-02-26). Spawns a
              fresh row in `pass_vectors[]` instead of overwriting the
              legacy singular `clip.pass_vector`. Disabled while
              another redraw is open so the calibration canvas only
              ever has one editor on it. */}
          <Button
            size="sm"
            variant="ghost"
            data-testid={`pv-add-new-${clip.id}`}
            onClick={onAddNew}
            disabled={creatingNew || !!editingId}
            title="Draw a new pass vector — appends to the candidate list, does NOT overwrite existing ones"
            className="h-7 px-2.5 ml-1 font-display font-bold uppercase tracking-widest text-[10px] bg-primary/15 text-primary border border-primary/40 hover:bg-primary hover:text-primary-foreground disabled:opacity-40"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add New Pass
          </Button>
        </div>
      </div>

      {/* Inline "draw new pass" overlay — mounted ABOVE the candidate
          list so the operator sees the existing rows + the fresh
          canvas at the same time. Mutually exclusive with the
          per-row edit overlay. */}
      {creatingNew && (
        <div
          ref={createOverlayRef}
          data-testid={`pv-create-overlay-${clip.id}`}
          className="px-3 py-3 border-b border-amber-300/30 bg-amber-300/5"
        >
          <PassVectorRedrawOverlay
            clip={clip}
            parent={parent}
            initialStart={null}
            initialEnd={null}
            releaseTimestampSeconds={liveClipLocalT}
            onNudgeFrame={onNudgeFrame}
            mode="create"
            onCancel={onCancelCreate}
            onSubmit={onCreateSubmit}
          />
        </div>
      )}

      {/* Bulk-Triage Control Bar — Phase A · Step 4 + 5 (Coach Accelerator). */}
      {unverifiedTotal > 0 && (
        <BulkTriageBar
          acceptThreshold={acceptThreshold}
          onAcceptThresholdChange={setAcceptThreshold}
          acceptEligibleCount={acceptEligible.length}
          rejectThreshold={rejectThreshold}
          onRejectThresholdChange={setRejectThreshold}
          rejectEligibleCount={rejectEligible.length}
          unverifiedTotal={unverifiedTotal}
          onBulkVerify={onBulkVerify}
          onBulkReject={onBulkReject}
          busy={bulkBusy}
        />
      )}

      {loading && vectors.length === 0 ? (
        <div className="px-4 py-6 text-center text-muted-foreground text-xs">
          <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
          Loading…
        </div>
      ) : vectors.length === 0 && !creatingNew ? (
        <div className="px-4 py-6 text-center text-muted-foreground text-xs font-mono">
          No pass vectors yet. Run AI tracking to generate auto-proposals,
          or click <span className="text-primary font-bold">+ Add New Pass</span> above
          to draw one manually.
        </div>
      ) : vectors.length === 0 ? (
        /* Hidden when creatingNew is open — the overlay itself is the
           only UI surface during the very first manual draw. */
        null
      ) : (
        <ul className="divide-y divide-border">
          {vectors.map((v, idx) => (
            <PassVectorRow
              key={v.id}
              vector={v}
              index={idx + 1}
              parent={parent}
              clip={clip}
              busy={busyId === v.id}
              editing={editingId === v.id}
              disabledByCreate={creatingNew}
              onAccept={() => onAccept(v)}
              onReject={() => onReject(v)}
              onEdit={() => onEdit(v)}
              onDelete={() => onDelete(v)}
              onCancelEdit={onCancelEdit}
              onCorrected={(start, end) => onCorrected(v, start, end)}
              onRetriggerHarvest={() => onRetriggerHarvest(v)}
              onAnnotationChanged={() => { refresh(); onMutated?.(); }}
              onNudgeFrame={onNudgeFrame}
              liveClipLocalT={liveClipLocalT}
              onJumpToRelease={() => {
                if (typeof v.timestamp_seconds === "number" && onJumpToRelease) {
                  onJumpToRelease(v.timestamp_seconds);
                  toast.success(`Jumped to release moment · t=${v.timestamp_seconds.toFixed(2)}s (clip-local)`);
                }
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
